import React from 'react';
import { useThemeSettings } from '@/contexts/ThemeContext';

// Simple child avatar component (same as in cards)
const ChildAvatar = ({ child, size = 'md', isActive = false, compact = false }) => {
  const { theme } = useThemeSettings();
  const isDarkTheme = theme === 'dark';
  
  // Size classes: compact mode uses smaller sizes for chips
  const sizeClasses = {
    sm: compact ? 'w-6 h-6 md:w-7 md:h-7' : 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16'
  };
  
  const textSizes = {
    sm: compact ? 'text-[10px] md:text-xs' : 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };
  
  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '??';
    return name
      .trim()
      .split(' ')
      .filter(n => n.length > 0)
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '??';
  };
  
  const photoUrl = child?.photo_url;
  const initials = getInitials(child?.name);
  // No active border for compact chip layout
  const activeBorder = (!compact && isActive) ? 'ring-4 ring-brand-500 ring-offset-2' : '';
  const paddingSize = compact ? 'p-0.5' : (size === 'sm' ? 'p-0.5' : size === 'md' ? 'p-0.5' : 'p-1');
  
  const gradientStyle = {
    background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.75) 0%, rgba(13, 148, 136, 0.75) 25%, rgba(104, 94, 255, 0.75) 50%, rgba(236, 72, 153, 0.75) 75%, rgba(245, 158, 11, 0.75) 100%)'
  };
  
  if (photoUrl) {
    return (
      <div className={`relative ${activeBorder} rounded-full ${paddingSize}`} style={gradientStyle}>
        <div className={`${sizeClasses[size]} rounded-full overflow-hidden ${isDarkTheme ? 'bg-slate-900' : 'bg-white'}`}>
          <img
            src={photoUrl}
            alt={child?.name || 'Child'}
            className="w-full h-full object-cover rounded-full"
            onError={(e) => {
              e.target.style.display = 'none';
              const parent = e.target.parentElement;
              if (parent) {
                const fallback = document.createElement('div');
                fallback.className = `w-full h-full rounded-full flex items-center justify-center`;
                fallback.innerHTML = `<span class="${textSizes[size]} font-semibold ${isDarkTheme ? 'text-slate-300' : 'text-slate-700'}">${initials}</span>`;
                parent.appendChild(fallback);
              }
            }}
          />
        </div>
      </div>
    );
  }
  
  return (
    <div className={`relative ${activeBorder} rounded-full ${paddingSize}`} style={gradientStyle}>
      <div className={`${sizeClasses[size]} rounded-full flex items-center justify-center ${isDarkTheme ? 'bg-slate-900' : 'bg-white'}`}>
        <span className={`${textSizes[size]} font-semibold ${isDarkTheme ? 'text-slate-300' : 'text-slate-700'}`}>{initials}</span>
      </div>
    </div>
  );
};

/**
 * ChildMatchFilter Component
 * Displays children as clickable profile picture circles for filtering matches
 */
const ChildMatchFilter = ({ 
  children = [], 
  matches = [],
  selectedChildId = null,
  onChildSelect,
  className = ''
}) => {
  const { theme } = useThemeSettings();
  const isDarkTheme = theme === 'dark';
  
  // Calculate match counts per child
  const getMatchCount = (childId) => {
    if (!childId) return matches.length; // "All" option
    return matches.filter(match => 
      match.matchedChildren?.some(child => {
        // Match by childId (could be child.id, child.name, or child.childId)
        // Use same logic as PerfectMatchesPage for consistency
        const matchedChildId = child.childId || child.id || child.name;
        return String(matchedChildId) === String(childId);
      })
    ).length;
  };
  
  const handleChildClick = (childId) => {
    onChildSelect?.(childId);
  };
  
  if (!children || children.length === 0) {
    return null;
  }
  
  return (
    <div className={`${className} ${isDarkTheme ? 'bg-slate-800' : 'bg-gray-50'} border-b ${isDarkTheme ? 'border-slate-700' : 'border-gray-200'}`}>
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center gap-1.5 md:gap-2 overflow-x-auto scrollbar-hide">
          {/* "All" option */}
          <button
            onClick={() => handleChildClick(null)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-full font-medium 
              whitespace-nowrap transition-all active:scale-95
              h-9 md:h-10 flex-shrink-0
              ${selectedChildId === null
                ? 'bg-brand-600 text-white shadow-sm'
                : `${isDarkTheme 
                  ? 'bg-slate-700 border border-slate-600 text-slate-200 hover:border-purple-400 hover:bg-purple-900/30' 
                  : 'bg-white border border-gray-200 text-gray-700 hover:border-purple-400 hover:bg-purple-50'}`
              }
            `}
          >
            <span className="text-sm">All</span>
            <span className={`text-xs opacity-80 hidden md:inline`}>
              {getMatchCount(null)}
            </span>
          </button>
          
          {/* Child options */}
          {children.map((child) => {
            const childId = child.id || child.name;
            const isSelected = selectedChildId === childId;
            const matchCount = getMatchCount(childId);
            
            return (
              <button
                key={childId}
                onClick={() => handleChildClick(childId)}
                className={`
                  flex items-center gap-2 rounded-full font-medium 
                  whitespace-nowrap transition-all active:scale-95
                  h-9 md:h-10 flex-shrink-0
                  ${isSelected
                    ? 'bg-purple-600 text-white shadow-sm px-4 py-2'
                    : `${isDarkTheme
                      ? 'bg-slate-700 border border-slate-600 text-slate-200 hover:border-purple-400 hover:bg-purple-900/30 px-3 py-2'
                      : 'bg-white border border-gray-200 text-gray-700 hover:border-purple-400 hover:bg-purple-50 px-3 py-2'}`
                  }
                `}
              >
                <ChildAvatar 
                  child={child} 
                  size="sm" 
                  isActive={isSelected}
                  compact={true}
                />
                <span className="text-sm truncate max-w-[120px] md:max-w-none">{child.name}</span>
                <span className={`text-xs opacity-80 hidden md:inline`}>
                  {matchCount}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ChildMatchFilter;

